import { useState, useEffect, useRef } from 'react';
import { Phone } from 'lucide-react';
import { useVoxloudClickToCall, type VoxloudConfigOption } from './useVoxloudClickToCall';

interface ClickToCallButtonProps {
  phoneNumber: string;
  className?: string;
  size?: 'sm' | 'md';
}

/**
 * Click-to-Call button via Voxloud.
 * Visibile se l'org ha almeno una config Voxloud attiva.
 * Con multi-config mostra un popover per scegliere da quale numero chiamare.
 * Caricamento config e avvio chiamata vivono in useVoxloudClickToCall.
 */
export function ClickToCallButton({ phoneNumber, className = '', size = 'sm' }: ClickToCallButtonProps) {
  const { configs, calling, startCall } = useVoxloudClickToCall();
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  // Chiudi picker al click esterno
  useEffect(() => {
    if (!showPicker) return;
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setShowPicker(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showPicker]);

  if (configs.length === 0 || !phoneNumber) return null;

  const initiateCall = (cfg: VoxloudConfigOption) => {
    setShowPicker(false);
    void startCall(cfg, phoneNumber);
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (calling) return;

    if (configs.length === 1) {
      initiateCall(configs[0]);
    } else {
      setShowPicker(!showPicker);
    }
  };

  const sizeClasses = size === 'sm' ? 'w-7 h-7' : 'w-9 h-9';
  const iconSize = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';

  return (
    <div className="relative" ref={pickerRef}>
      <button
        onClick={handleClick}
        disabled={calling}
        title={`Chiama ${phoneNumber} via Voxloud`}
        className={`inline-flex items-center justify-center rounded-full border border-green-200 bg-green-50 text-green-600 hover:bg-green-100 hover:border-green-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${sizeClasses} ${className}`}
      >
        <Phone className={`${iconSize} ${calling ? 'animate-pulse' : ''}`} />
      </button>

      {showPicker && (
        <div className="absolute right-0 top-full mt-1 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 min-w-[200px]">
          <div className="px-3 py-1.5 text-xs text-gray-500 dark:text-gray-400 font-medium">
            Chiama da:
          </div>
          {configs.map((cfg) => (
            <button
              key={cfg.config_id}
              onClick={(e) => { e.stopPropagation(); initiateCall(cfg); }}
              className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="text-sm font-medium text-gray-900 dark:text-white">{cfg.label || 'Centralino'}</div>
              {cfg.phone && <div className="text-xs text-gray-500 dark:text-gray-400">{cfg.phone}</div>}
              {cfg.userName && <div className="text-xs text-gray-400 dark:text-gray-500">{cfg.userName}{cfg.extension ? ` (int. ${cfg.extension})` : ''}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
